/**
 * @file axiosInstance.ts
 * @description Création d'une instance Axios pour faciliter les requêtes HTTP à l'API backend,
 * incluant la gestion des jetons d'accès, du rafraîchissement, et la normalisation des erreurs HTTP.
 */
import axios, { HttpStatusCode } from 'axios';
import { baseUrl } from './env';
import { useAuthStore } from '../store/auth/useAuthStore';
import { normalizeHttpError } from '../errors/httpErrors';

const SKIP_REFRESH_URLS = ['api/auth/signin', 'api/auth/refresh-token'];

function shouldSkipRefresh(url?: string) {
  return !!url && SKIP_REFRESH_URLS.some((u) => url.includes(u));
}

// Créer une instance Axios
const axiosInstance = axios.create({
  baseURL: baseUrl, // URL de base de votre API backend
  withCredentials: true, // Assurez-vous que les cookies sont envoyés avec les requêtes
});

// Intercepteur de requêtes pour attacher le header Authorization
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    throw normalizeHttpError(error);
  },
);

// Intercepteur de réponses pour gérer le rafraîchissement des jetons et normaliser les erreurs
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url;

    // Tentative de rafraîchissement du jeton si 401 et pas déjà en retry
    if (
      status === HttpStatusCode.Unauthorized &&
      originalRequest &&
      !originalRequest._retry &&
      !shouldSkipRefresh(requestUrl)
    ) {
      originalRequest._retry = true;

      try {
        // Effectuer une requête pour obtenir un nouveau jeton d'accès
        const refreshToken = useAuthStore.getState().refreshToken;
        const { data } = await axios.post(
          baseUrl + '/api/auth/refresh-token',
          { refreshToken },
          { withCredentials: true },
        );
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
        useAuthStore.getState().setAccessToken(data.accessToken); // Mettre à jour le jeton d'accès dans le store
        return axiosInstance(originalRequest); // Réessayer la requête initiale avec le nouveau jeton
      } catch (_refreshError) {
        // Le refresh a échoué : invalider les jetons et rediriger
        useAuthStore.getState().setAccessToken('');
        useAuthStore.getState().setRefreshToken('');
        if (globalThis.window !== undefined) {
          globalThis.location.assign('/auth');
        }
        throw normalizeHttpError(_refreshError);
      }
    }
    // Toute autre erreur : normaliser et rejeter
    throw normalizeHttpError(error);
  },
);

export default axiosInstance;
